import React, { useContext, useEffect, useState } from "react";
import { useHistory, Link } from "react-router-dom";
import FormatDate from "core/utils/FormatDate";
import FormatCurrency from "core/utils/FormatCurrency";
import FormatNumber from "core/utils/FormatNumber";
import SessionContext from "SessionContext";
import AuthTokenContext from "AuthTokenContext";
import ComplexFilterBuilder from "./complex-filter-builder";
import { ConfirmNoInvoice } from "./order";
import ConfirmSuccess from "../core/ui/confirm-success";
import TooltipOnhover from "../partials/tooltip-onhover";
import { Button, Card, CardHeader, CardBody, Label, Table, Modal, ModalHeader, ModalBody, ModalFooter, Col, Row, Alert, Badge } from "reactstrap";
import EditNameAndDescription from "../partials/edit-name-description";
import Chart from "core/ui/charts/index";
import { existingComplexFilters, existingSimpleFilters } from "../core/utils/data-helper-functions";
import isEqual from "lodash.isequal";

// channel_options names as returned by the API, used to match against the channelOption keys (direct/email/digital/analytics)
const channel_option_name_match = {
  direct: "direct mail",
  email: "email",
  digital: "custom digital",
  analytics: "analytics",
}

export default function OrderDetails(props) {
  const session = useContext(SessionContext);
  const tokens = useContext(AuthTokenContext);
  const access = session.appState.userCan;
  const history = useHistory();

  const [order, setOrder] = useState();
  const [activeFilters, setActiveFilters] = useState();
  const [isComplex, setIsComplex] = useState();
  const [errorModalIsOpen, setErrorModalIsOpen] = useState(false);
  const [complexFilters, setComplexFilters] = useState();
  const [complexFields, setComplexFields] = useState();
  const [orderIsExpired, setOrderIsExpired] = useState(false);
  const [orderDaysToExpire, setOrderDaysToExpire] = useState(0);
  const [daysLeftToExpire, setDaysLeftToExpire] = useState(0);
  const [downloadFileName, setDownloadFileName] = useState("");
  const orderExpirationTime = window.order_expiration_days;
  const toolTipOrder = `Orders are available for download within ${orderExpirationTime} days of purchase.`;
  const [deliveryType, setDeliveryType] = useState("Download");
  const [showNoInvoiceModal, setShowNoInvoiceModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [succesLabel, setSuccessLabel] = useState("Saved!");
  const [showEditModal, setShowEditModal] = useState(false);
  const [savedSelectionList, setSavedSelectionList] = useState();
  const [isDoNotInvoice, setIsDoNotInvoice] = useState(false);
  const [isDeliveryOptionDownload, setIsDeliveryOptionDownload] = useState(false);
  const [dataFiltersHaveChanged, setDataFiltersHaveChanged] = useState(false);
  const [noFilters, setNoFilters] = useState(false);

  useEffect(() => {
    setDeliveryType(session.appState.activeCompany.delivery_type);
    session.crossfilterStore.set_missing_filters(false);
    // eslint-disable-next-line
  }, []);

  // Check saved filters against existing filters
  function checkForExistingFilters() {
    if (!order) return;
    // If charts are loaded for the wrong population domain, skip entirely —
    // comparing against wrong-domain charts would flag valid filters as missing.
    if (order.population_domain) {
      const loadedDomain = session.crossfilterStore.explorerModule || "consumers";
      if (order.population_domain !== loadedDomain) return;
    }
    // Charts not ready yet — return the order's saved data so the UI isn't blank,
    // but skip the comparison to avoid false "filters changed" positives.
    if (!session.chartsStore.loaded || !session.chartsStore.charts) {
      setDataFiltersHaveChanged(false);
      return order.is_complex ? order.complex_filters : Object.entries(order.filters || {});
    }
    let missing = false;
    let availableFilters;
    if (order.is_complex) {
      const charts = session.chartsStore.charts;
      availableFilters = JSON.parse(JSON.stringify(order.complex_filters));
      availableFilters = existingComplexFilters(order.complex_filters, charts, availableFilters);
      missing = !isEqual(availableFilters.tree.children1, order.complex_filters.tree.children1);
      const no_filters = availableFilters && !Object.keys(availableFilters.tree.children1).length;
      session.crossfilterStore.set_complex_filters(availableFilters);
      setComplexFilters(availableFilters);
      setNoFilters(no_filters);
    } else {
      const charts = Object.entries(session.chartsStore.charts);
      availableFilters = JSON.parse(JSON.stringify(order.filters));
      availableFilters = existingSimpleFilters(order.filters, charts, availableFilters);
      missing = !isEqual(availableFilters, Object.entries(order.filters));
      setNoFilters(availableFilters && !availableFilters.length);
    }
    setDataFiltersHaveChanged(missing);
    return availableFilters;
  }

  // TODO: Hack for now, need to add to useQuery
  const downloadOrderData = () => {
    fetch(`${window.api_url}/query/order/download?id=${props.args.order}`, {
      headers: {
        "X-Active-Company": `${session.appState.activeCompany.id}`,
        Authorization: `Bearer ${tokens.accessToken}`,
      },
    }).then((response) => {
      response.blob().then((blob) => {
        let url = window.URL.createObjectURL(blob);
        url = encodeURI(url);
        let a = document.createElement("a");
        a.href = decodeURI(url);
        a.download = `AM_${props.args.order}_${cleanFileName(order.name)}.zip`;
        a.click();
      });
    });
  };

  const cleanFileName = (name) => {
    const regex = /[^a-zA-Z0-9]/ig;
    const clean_order_name = name.replaceAll(regex, "_");
    return clean_order_name;
  }

  useEffect(() => {
    if(order) {
      setDownloadFileName(`AM_${props.args.order}_${cleanFileName(order.name)}.zip`);
      checkForExistingFilters();
    }
    if(order && "delivery_options" in order) {
      let hasDownload = JSON.stringify(order.delivery_options).includes("Download");
      setIsDeliveryOptionDownload(hasDownload);
    }
    // eslint-disable-next-line
  }, [order, props.args.order, session.chartsStore.loaded])

  useEffect(() => {
    if (order) {
       let availableFilters;
       if (order.is_complex) {
         const updatedFilters = checkForExistingFilters();
         setIsComplex(order.is_complex);
         if (updatedFilters) {
           setComplexFilters(updatedFilters);
         }
         if (session.chartsStore.loaded) {
           session.crossfilterStore.getComplexFields().then((_complexFields) => {
             setComplexFields(_complexFields);
           });
         }
        } else {
          "saved_selection_criteria" in order && setSavedSelectionList(order.saved_selection_criteria);
          availableFilters = checkForExistingFilters();
          if (availableFilters) {
            setActiveFilters(
              availableFilters
              .filter(([chart_id, filters]) => !!filters)
              .map(([chart_id, filters]) => {
                return [chart_id, filters];
              })
            );
          }
        }
      setIsDoNotInvoice(order.do_not_invoice);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, session.chartsStore.loaded]);

  useEffect(() => {
    const checkOrderAge = (order_date) => {
      if(order_date !== null) {
        const orderDate = new Date(order_date);
        const todayDate = new Date();
        // calculate the time difference
        var timePassed = todayDate.getTime() - orderDate.getTime();
        // calculate the days between order date and today
        var daysPassed = timePassed / (1000 * 3600 * 24);
        setOrderDaysToExpire(Math.ceil(daysPassed));
        if(Math.ceil(daysPassed) > orderExpirationTime) {
          setOrderIsExpired(true);
        } else {
          setOrderIsExpired(false);
        }
      }
    }

    if (props.args.order) {
      session.query("order/get", { id: props.args.order }).then((order) => {
        setOrder(order);
        checkOrderAge(order.order_date);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDaysLeftToExpire(orderExpirationTime - orderDaysToExpire);
  }, [orderDaysToExpire, orderExpirationTime]);

  function onClickDownload(ev) {
    downloadOrderData();
  }

  function toggleModal() {
    setErrorModalIsOpen(!errorModalIsOpen);
  }

  function onSubmitNoInvoice(reason) {
    setShowNoInvoiceModal(false);
    let orderData = {
      order_id: props.args.order,
      reason
    }
    
    session.command("order/save_order_do_not_invoice", 
                     orderData, 
                     {}, { defaultErrorHandling: false })
            .then(() => {
              setShowSuccess(true);
              setSuccessLabel("Order Submitted.");
              setTimeout(() => {
                history.push('/orders');
              }, 2000);
            })
            .catch((error) => {
              console.log("Failed to Submit Order", JSON.stringify(error));
            })
  }

  function onShowNoInvoiceModal(ev) {
    setShowNoInvoiceModal(true);
  }

  const toggleEditNameModal = () => {
    setShowEditModal(!showEditModal);
  };

  function onUpdateName(_name, _description) {
    let updatedOrder = {...order, name: _name, description: _description};

    session.command("order/rename",
      updatedOrder, {}, { defaultErrorHandling: false }
    )
    .then((res) => {
      toggleEditNameModal();
      setShowSuccess(true);
      setSuccessLabel("Order Updated.");
      setTimeout(() => {
        history.push("/orders");
      }, 2000);
    })
    .catch((error) => {
      console.log("Failed to update Order", error)
    })
  }

  const selectedCriteriaBuilder = () => {
    if (!savedSelectionList) {
      return "";
    }
    const charts = Object.entries(session.chartsStore.charts);
    let availableFilters = JSON.parse(JSON.stringify(order.filters));
    availableFilters = existingSimpleFilters(order.filters, charts, availableFilters);

    let savedFeatureIds = [];
    let savedFeatureNames = [];
    let availableFeatureIds = [];
    let availableFeatureNames = [];
    savedSelectionList.forEach((feature) => {
      feature.id && savedFeatureIds.push(feature.id);
      feature.filter_name && savedFeatureNames.push(feature.filter_name);
    });
    availableFilters.forEach((feature) => {
      feature[0] && availableFeatureIds.push(feature[0]);
      feature.filter_name && availableFeatureNames.push(feature.filter_name);
    });
    
    savedSelectionList.forEach(feature => { 
      if (savedFeatureIds.length && availableFeatureIds.length && feature.id) {
        feature.missing = !availableFeatureIds.includes(feature.id);
      } else if(savedFeatureNames.length && availableFeatureNames.length && feature.filter_name) {
        feature.missing = !availableFeatureNames.includes(feature.filter_name);
      }
    });

    // format value_labels into an array for display
    const criteria_list = savedSelectionList.map(item => {
      if (!item.filter_values) return null;
      return {
        ...item,
        filter_values: Array.isArray(item.filter_values)
          ? item.filter_values
          : Object.values(item.filter_values)
      };
    }).filter(Boolean);
    
    return criteria_list.map((val) => {
      if(!val.filter_values) { return "";}
      return (
        <div key={val.filter_name}>
          <div className="view-only card">
            {val.missing &&
              <div className="card-header">
                    <div className="missing-feature">
                      <Badge color="warning"><i className="bi bi-exclamation-triangle-fill"></i> Feature no longer available</Badge>
                    </div>
              </div>
            }
            <div className="chart-card card-body">
              <figure className="chart-widget is-view-only pie-chart">
                <figcaption className="heading" draggable="false">
                  <span>
                    <span className="hovertext" data-hover={val.filter_name}>
                      {val.is_excluded && "Excluded:"} {val.filter_name}
                    </span>
                  </span>
                  <i className="spacer"></i>
                  <div className="actions"></div>
                </figcaption>
                <div className="summary">
                  <ul>
                    {val.filter_values &&
                      val.filter_values.map((item) => {
                        return <li key={item.value || item}>{item.value || item}
                          {item.match_value && <span className='match-level-details'><span className='label'>Match Level: </span><span className='value'>{item.match_value}</span></span>}
                        </li>;
                      })}
                  </ul>
                </div>
              </figure>
            </div>
          </div>
        </div>
      );
    });
  }

  const order_name = (order && order.id !== "") ? `${order.name} - #${order.id}` : "";

  if (!order) return <div></div>;

  return (
    <div className="page">
      <div className="list-header sticky-page-header flex-header">
        <div className="header-link-back">
          <Link to="/orders">
            <Button type="button" className="new-button">
              <i className="bi bi-chevron-left"></i> Orders
            </Button>
          </Link>
        </div>

        <div className="header-name">
          <TooltipOnhover labelText={order_name} />
        </div>

        <div className="order-actions-dropdown">
          {access('order_edit') && <>
            <Button
              type="button"
              onClick={toggleEditNameModal}
              className="new-button"
              >
              Edit Name/Description
            </Button>
            <span className="m-2"></span>
          </>}
          {access('administration') && <>
            <Button
              type="button"
              onClick={onShowNoInvoiceModal}
              disabled={isDoNotInvoice}
              className="new-button"
            >
              Do Not Invoice
            </Button>
          </>}
        </div>
      </div>
      <div className="page order-page-wrapper">
        <section className="main-content">
          <div className="order">
            <Card className="model-charts">
              <CardHeader>
                <div>
                  <div className="summary-items">
                    <div className="labeled-items horizontal">
                      <div className="labeled-item primary">
                        <Label>Order Name:</Label>
                        <span className="value">{order.name}</span>
                      </div>
                      <div className="labeled-item">
                        <Label>Order Number:</Label>
                        <span className="value">{order.id}</span>
                      </div>
                        <div className="labeled-item">
                        <Label>Creation Date:</Label>
                        <span className="value">
                          <FormatDate date={order.created_date} />
                        </span>
                      </div>
                      <div className="labeled-item description">
                        <Label>Description:</Label>
                        <span className="value">{(order.description !== "") ? order.description : "--" }</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col md={6}>  
                    <div className="audience-summary">
                      <div className="summary details">
                          <h3>Audience Counts</h3>
                          <div className="selection">
                            <Card>
                              <CardBody>
                                <div className="starting-quantity labeled-items horizontal center">
                                  <div className="labeled-item center">
                                    <label>Starting Quantity:</label>
                                    <div className="value large">
                                      <FormatNumber value={order.original_quantity} />
                                    </div>
                                  </div>
                                  <div className="labeled-item center">
                                    <label>Dedupe Level:</label>
                                    <div className="value large">
                                      {order.dedupe_level ? order.dedupe_level.replace(/^LE_/,"") : "Individual"}
                                    </div>
                                  </div>
                                </div>
                              </CardBody>
                            </Card>
                        </div>
                      </div>
                      <div className="criteria text-center">
                        <h3 className="text-start">Selection Criteria</h3>
                         <Alert
                            isOpen={dataFiltersHaveChanged}
                            color="warning"
                            className="d-flex"
                            >
                            <i className="bi bi-exclamation-triangle-fill"></i>
                            <div>
                                Since the time this order was placed, one or more of the filters chosen are no longer available.
                            </div>
                          </Alert>
                        { savedSelectionList ? selectedCriteriaBuilder() :
                        activeFilters &&
                          activeFilters.map((filter) => (
                            <Chart
                              summarize
                              chart_id={filter[0]}
                              key={filter[0]}
                              filters={filter[1]}
                              viewOnly
                            />
                          ))
                        }
                        {noFilters && <Card className="p-3">No selected criteria found.</Card>}
                        {!noFilters && isComplex && complexFields && (
                          <div className="mt-5"><Button onClick={toggleModal}>Complex Filters</Button></div>
                        )}
                      </div>
                    </div>
                  </Col>
                <Col md={6}>
                <div className="main-summary">
                  <div className="audience-selection">
                    <h3>Order Summary</h3>
                      <div className="summary">
                      <div className="selection">
                        <Card>
                          <CardBody>
                            <div className="starting-quantity labeled-items horizontal center">
                              <div className="labeled-item center">
                                <label>Desired Quantity:</label>
                                <div className="value large">
                                  <FormatNumber value={order.quantity} />
                                </div>
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                          <div className="channel-options card">
                            <div className="channel-title">
                              Channel Options
                            </div>
                            <div className="channels-list">
                              {Object.entries(order.channel_option).map(([key, value])=> {
                                let names = {
                                  "direct": "Direct Mail", "email": "Email Deployment", "digital": "Digital Onboarding", "analytics": "Analytics Only"
                                }
                                if(value) {
                                  return <span key={key} className="channel-text">
                                  <i className="bi bi-check-square"/> {names[key]}
                                </span>;
                                }
                                return "";
                              })}
                            </div>
                          </div>

                          <CutbackSummarySection cutbackInfo={order.saved_cutback_field_prioritization} />
                          <DeliveryOptionsSection order={order} />
                        </div>
                      </div>
                      </div>  
                      <div className="additional-fields">
                        <h3>Additional Order Data Fields</h3>
                        <Card>
                          <CardBody>
                        <div className="additional-data-selected">
                          {order.fields.length > 0 &&
                              <ul>
                                {order.fields &&
                                  order.fields.map((f) => <li key={f.id}>{f.label}</li>)}
                              </ul>
                          }
                          {order.fields.length === 0 &&
                            <p className="text-center">No additional fields selected. </p>
                          }
                            </div>
                          </CardBody>
                        </Card>
                    </div>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
          </div>
        </section>

        <section className="order-overview">
          <h1>Order Details</h1>
          <Card className="order-cart">
            {isDoNotInvoice && 
               <Alert
                  color="warning"
                  className="d-flex"
                >
                  <i className="bi bi-info-circle-fill"></i>
                  <div>
                      This order has been marked as <strong>Do Not Invoice</strong>.
                  </div>
                </Alert>
            }
            <div className="total-cost">
              <FormatCurrency value={order.total_cost} />
            </div>
            <Table className="line-items">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Channel</th>
                  <th>Qty</th>
                  <th>@</th>
                  <th>Cost</th>
                </tr>
              </thead>
              <tbody>
                {order.invoice_line_items &&
                  order.invoice_line_items.map((item) => (
                    <tr key={item.invoice_category.id}>
                      <td>{item.invoice_category.name}</td>
                      <td>{
                        item.invoice_category.channel_options.map((option) => {
                          let selectedOnly = [];
                          let lowerName = option.name.toLowerCase();
                          let matchedKey = Object.keys(channel_option_name_match).find((_ch) => lowerName.startsWith(channel_option_name_match[_ch]));
                          Object.keys(order.channel_option).forEach((_ch) => {
                            if(order.channel_option[_ch]) {
                              selectedOnly.push(_ch);
                            }
                          });
                          /* only show channel names selected */
                          if(!selectedOnly.includes(matchedKey)) {
                            return "";
                          }
                          return <span key={option.id}><span>&bull; {option.name}</span><br /></span>
                        })
                      }</td>
                      <td>
                        <FormatNumber value={item.quantity} />
                      </td>
                      <td>
                        <FormatCurrency value={item.unit_price} />
                      </td>
                      <td>
                        <FormatCurrency value={item.total_price} />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
            {(order.status === "Complete" || order.status === "Downloaded") && 
              (deliveryType === "Download" || isDeliveryOptionDownload) && (access('order_download'))
                  && (
                    <div className="order-download-section text-center mt-3">
                    <Button className="promote" type="button" onClick={onClickDownload} disabled={orderIsExpired}>
                      <i className="fa-solid fa-down-to-line"></i>
                      <span style={{textAlign: "center"}}>Download Audience File</span>
                    </Button>
                    {orderIsExpired && (
                      <div className="form-field-error mt-2" title={toolTipOrder}>
                        This order has expired.
                      </div>
                    )} 
                    {!orderIsExpired && (order.order_date !== null) && (daysLeftToExpire !== 0) && (
                      <div className="form-field-error mt-2" title={toolTipOrder}>
                        {/* {deliveryType} -  */}
                        This order expires in {daysLeftToExpire} {daysLeftToExpire === 1 ? "day" : "days"}.
                      </div>
                    )}
                    {!orderIsExpired && (order.order_date !== null) && (daysLeftToExpire === 0) && (
                      <div className="form-field-error mt-2" title={toolTipOrder}>
                        This order expires today.
                      </div>
                    )}
                  </div>
                )}
                {deliveryType === "SpeedeonSFTP" && (
                  <div className="order-download-message">
                    Order file:<br />
                    <b>{downloadFileName}</b> 
                    <br />is available in the directory of your Speedeon SFTP account.
                  </div>
                )}
          </Card>
          <div className="notes">
            <h3>Notes</h3>
            {/* 
            // TODO: Isaac Note. LEAVE for future editing currently doesn't work and we are redoing with AM-515
            {access('order_edit') &&
              <>
              <textarea
                name="order-notes"
                id="order-notes"
                readOnly
                value={order.notes}
                />
              <div className="actions">
                <Button size="sm" type="button">
                  <i className="fa-solid fa-pencil"></i>Edit Notes
                </Button>
                <Button size="sm" type="button" disabled>
                  Save
                </Button>
              </div>
              </>
            } */}
            {access('order_view') === true &&
              <p>{order.notes || "No saved notes."}</p>
            }
          </div>
        </section>
      </div>
      <Modal
        centered
        size="lg"
        isOpen={errorModalIsOpen}
        >
        <ModalHeader toggle={toggleModal}>
          <em>Complex Filters</em>
        </ModalHeader>
        <ModalBody>
          <ComplexFilterBuilder
            initialQuery={
              isComplex && complexFilters && complexFilters.tree
                ? complexFilters.tree
                : null
            }
            fields={complexFields}
            readonly={true}
          />
        </ModalBody>
        <ModalFooter>
          <Button onClick={toggleModal}>Close</Button>
        </ModalFooter>
      </Modal>
      <ConfirmNoInvoice 
        show={showNoInvoiceModal}
        onHide={()=> setShowNoInvoiceModal(!showNoInvoiceModal)}
        onSubmit={onSubmitNoInvoice}
      />
      <EditNameAndDescription 
        toggleShowModal={toggleEditNameModal}
        data={{name: order.name, description: order.description}}
        confirmChanges={onUpdateName}
        showModal={showEditModal}
      />
      <ConfirmSuccess show={showSuccess} label={succesLabel} />
    </div>
  );
}

/**
 * 
 */
const CutbackSummarySection = ({cutbackInfo}) => {
  const [label, setLabel] = useState();
  const [values, setValues] = useState();
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (cutbackInfo && Object.keys(cutbackInfo).length) {
      setLabel(cutbackInfo.label);
      setValues(cutbackInfo.values);
    } else {
      setLabel("None");
      setValues([]);
    }
    // eslint-disable-next-line
  }, [cutbackInfo])

  useEffect(() => {
    if(label && label.length) {
      setLoaded(true);
    }
    // eslint-disable-next-line
  }, [label, values])

  if(!loaded) {
    return "";
  }

  return (
    <div className="channel-options card">
      <div className="channel-title">
        Cutback Priority
      </div>
      <div className="cutback-wrapper">
        <div className="cutback-label">{label}</div>
        <div className="cutback-values">
          <ul>
            {values.map((value, index) => (
              <li key={index}>{value}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

const DeliveryOptionsSection = ({ order }) => {
  if (!order?.delivery_options?.length) return null;

  return (
    <div className="channel-options card">
      <div className="channel-title">Delivery Options</div>
      <div className="channels-list">
        {order.delivery_options.map((opt) => (
          <span key={opt.id} className="channel-text">
            <i className="bi bi-check-square" /> {opt.name}
            {opt.id === 4 &&
              Array.isArray(order.census_destinations) &&
              order.census_destinations.length > 0 && (
                <ul className="census-destinations">
                  {order.census_destinations.map((dest) => (
                    <li key={dest.id ?? dest}>
                      <i className="bi bi-caret-right-fill"></i> {dest.name ?? String(dest)}
                    </li>
                  ))}
                </ul>
              )}
          </span>
        ))}
      </div>
    </div>
  );
};

